import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Radio,
  Space,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  ImportOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import {
  commitAssessmentImport,
  downloadAssessmentDefinition,
  previewAssessmentImport,
} from './api';
import { JsonBlock } from './shared';
import { AssessmentScenario, ImportPreview, JsonObject } from './types';

type Props = {
  scenario: AssessmentScenario;
  onImported: (createdCode?: string) => Promise<void> | void;
};

const ImportExport: React.FC<Props> = ({ scenario, onImported }) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<'definition' | 'benchmark'>('definition');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const targetLocked =
    mode === 'benchmark' && preview?.editable_target === false;

  const parseDocument = (): JsonObject => {
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch (_error) {
      throw new Error('Import content must be valid JSON.');
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('Import content must be a JSON object.');
    }
    return value as JsonObject;
  };

  const buildPayload = async () => {
    const document = parseDocument();
    if (mode === 'benchmark') return { mode, payload: document };
    const values = await form.validateFields();
    return {
      mode,
      definition: document,
      new_code: values.new_code.trim(),
      new_name: values.new_name?.trim() || undefined,
      new_model_version: values.new_model_version?.trim() || undefined,
    };
  };

  const previewImport = async () => {
    setPreviewing(true);
    try {
      const result = await previewAssessmentImport(await buildPayload());
      setPreview(result);
      message.success('Import validation completed without writing data.');
    } catch (requestError) {
      if (!(requestError as any)?.errorFields) {
        message.error((requestError as Error).message);
      }
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const commitImport = async () => {
    setCommitting(true);
    try {
      const result = await commitAssessmentImport(await buildPayload());
      const createdCode = (result.scenario as AssessmentScenario | undefined)
        ?.code;
      message.success(
        mode === 'definition'
          ? `Imported ${createdCode || 'definition'} as a new draft.`
          : 'Benchmark payload imported through the existing composable ingest contract.',
      );
      setPreview(null);
      await onImported(createdCode);
    } catch (requestError) {
      if (!(requestError as any)?.errorFields) {
        message.error((requestError as Error).message);
      }
    } finally {
      setCommitting(false);
    }
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = event => {
      setText(String(event.target?.result || ''));
      setPreview(null);
    };
    reader.onerror = () => message.error('Could not read that local file.');
    reader.readAsText(file);
    return false;
  };

  return (
    <div className="assessment-import-grid">
      <Card title="Export" size="small">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Definition-only export"
          description="Exports include the exact scenario, instruments, periods, benchmark matrix, and benchmark news rows. Session links, evidence, job state, and results are separate and are never folded into a definition import."
        />
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadAssessmentDefinition(scenario.code, 'json').catch(error =>
                message.error(error.message),
              )
            }
          >
            Definition JSON
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadAssessmentDefinition(scenario.code, 'csv').catch(error =>
                message.error(error.message),
              )
            }
          >
            Definition CSV bundle
          </Button>
        </Space>
        <Typography.Paragraph type="secondary" className="assessment-help-copy">
          Result exports are session-specific and available from the Sessions
          &amp; results tab.
        </Typography.Paragraph>
      </Card>

      <Card title="Import JSON" size="small">
        <Radio.Group
          value={mode}
          onChange={event => {
            setMode(event.target.value);
            setPreview(null);
          }}
          optionType="button"
          buttonStyle="solid"
          options={[
            { value: 'definition', label: 'Full definition' },
            { value: 'benchmark', label: 'Benchmark payload' },
          ]}
        />
        <Alert
          className="assessment-callout"
          type={mode === 'definition' ? 'info' : 'warning'}
          showIcon
          title={
            mode === 'definition'
              ? 'Full definition import always creates a new draft'
              : 'Benchmark import uses the existing upsert contract'
          }
          description={
            mode === 'definition'
              ? 'The importer preserves explicit zero and null values, resolves referenced PM IDs exactly, and refuses an existing scenario code. It never imports links, evidence, or results.'
              : 'A new target is created as a draft. An existing target must be an unlinked draft. Incoming rows are composable upserts and omitted rows are not deleted.'
          }
        />
        <Form form={form} layout="vertical">
          {mode === 'definition' && (
            <div className="assessment-form-grid">
              <Form.Item
                name="new_code"
                label="New scenario code"
                rules={[{ required: true }]}
              >
                <Input placeholder={`${scenario.code}-import`} />
              </Form.Item>
              <Form.Item name="new_name" label="Name override (optional)">
                <Input />
              </Form.Item>
              <Form.Item
                name="new_model_version"
                label="Model version override (optional)"
              >
                <Input />
              </Form.Item>
            </div>
          )}
        </Form>
        <div className="assessment-table-toolbar">
          <Upload
            accept="application/json,.json"
            beforeUpload={readFile}
            showUploadList={false}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Choose JSON file</Button>
          </Upload>
          <Typography.Text type="secondary">
            The file stays local until Preview or Import is clicked.
          </Typography.Text>
        </div>
        <Input.TextArea
          value={text}
          onChange={event => {
            setText(event.target.value);
            setPreview(null);
          }}
          placeholder="Paste the exported definition or existing benchmark payload here"
          autoSize={{ minRows: 12, maxRows: 28 }}
          spellCheck={false}
        />
        <Space className="assessment-import-actions" wrap>
          <Button
            icon={<CheckCircleOutlined />}
            onClick={previewImport}
            loading={previewing}
            disabled={!text.trim()}
          >
            Preview (no writes)
          </Button>
          <Button
            type="primary"
            icon={<ImportOutlined />}
            onClick={commitImport}
            loading={committing}
            disabled={!preview || targetLocked}
          >
            {mode === 'definition'
              ? 'Create imported draft'
              : 'Import benchmark rows'}
          </Button>
        </Space>
        {preview && (
          <Card
            size="small"
            title="Validated import plan"
            className="assessment-inner-card"
          >
            <JsonBlock value={preview} />
          </Card>
        )}
        {targetLocked && (
          <Alert
            className="assessment-inner-card"
            type="error"
            showIcon
            title="The benchmark target is not editable"
            description="The existing target is active or already linked to a session. Import is disabled; use a new scenario code or a definition-only clone."
          />
        )}
      </Card>
    </div>
  );
};

export default ImportExport;
